import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ChatBot = ({ mode = 'brainstorming', context = {}, onClose, onBulletUpdate }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: mode === 'brainstorming'
        ? "Hi! I'm here to help you build your master resume. What would you like to work on? I can help you write better bullet points, articulate your experience, or suggest what to include."
        : "Hi! I'm your interview prep assistant. I'll ask you questions about your experience and the role you're applying for. Ready to practice?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { getToken } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: input,
          mode: mode,
          context: context,
          conversation_history: messages
        })
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = { role: 'assistant', content: data.response };

        // Check if AI provided an edited bullet
        if (data.edited_bullet && data.selected_bullet_info) {
          assistantMessage.edited_bullet = data.edited_bullet;
          assistantMessage.selected_bullet_info = data.selected_bullet_info;
          assistantMessage.original_bullet = data.original_bullet;  // Store original bullet text
        }

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.'
        }]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div data-chatbot style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--bg-elevated)',
      borderLeft: '1px solid var(--border-light)'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid var(--border-light)',
        background: 'var(--bg-surface)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
              {mode === 'brainstorming' ? 'Resume Assistant' : 'Interview Coach'}
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>
              {mode === 'brainstorming' ? 'Help build your resume' : 'Practice interview questions'}
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginLeft: '32px'
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Selected Bullet Indicator */}
      {context.selectedBullet && (() => {
        const { section, sectionIndex, bulletIndex } = context.selectedBullet;
        let bulletText = '';

        // Extract the bullet text from formData
        if (section === 'experience' && context.formData?.experience?.[sectionIndex]?.bullets?.[bulletIndex]) {
          bulletText = context.formData.experience[sectionIndex].bullets[bulletIndex];
        } else if ((section === 'project' || section === 'projects') && context.formData?.projects?.[sectionIndex]?.bullets?.[bulletIndex]) {
          bulletText = context.formData.projects[sectionIndex].bullets[bulletIndex];
        } else if (section === 'education' && context.formData?.education?.[sectionIndex]?.bullets?.[bulletIndex]) {
          bulletText = context.formData.education[sectionIndex].bullets[bulletIndex];
        }

        return bulletText ? (
          <div style={{
            padding: '12px 20px',
            background: 'rgba(99, 102, 241, 0.1)',
            borderBottom: '1px solid rgba(99, 102, 241, 0.3)',
            borderTop: '1px solid rgba(99, 102, 241, 0.3)'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--indigo-500)', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ✓ Selected Bullet Point
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.5' }}>
              {bulletText}
            </div>
          </div>
        ) : null;
      })()}

      {/* Conversational Context Indicator */}
      {!context.selectedBullet && (() => {
        // Check if there's a last edited bullet in conversation history
        const lastEditedMessage = [...messages].reverse().find(
          msg => msg.role === 'assistant' && msg.edited_bullet && msg.selected_bullet_info
        );

        if (lastEditedMessage) {
          return (
            <div style={{
              padding: '8px 20px',
              background: 'rgba(16, 185, 129, 0.08)',
              borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
              borderTop: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                💬 Conversational Context: I remember the last bullet we edited
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: '8px'
            }}
          >
            <div style={{
              maxWidth: '80%',
              padding: '12px 16px',
              borderRadius: '12px',
              background: msg.role === 'user'
                ? 'var(--gradient-primary)'
                : 'rgba(255, 255, 255, 0.05)',
              border: msg.role === 'user'
                ? 'none'
                : '1px solid var(--border-light)',
              color: 'var(--text-primary)',
              fontSize: '14px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {msg.content}
            </div>

            {/* Show edited bullet with accept/reject if present */}
            {msg.edited_bullet && msg.selected_bullet_info && (
              <div style={{
                maxWidth: '80%',
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(147, 51, 234, 0.08)',
                border: '2px solid rgba(99, 102, 241, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--indigo-500)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Suggested Edit
                </div>

                {/* Original vs New */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '4px', fontWeight: '600' }}>- Original:</div>
                    <div style={{
                      fontSize: '13px',
                      color: '#ef4444',
                      background: 'rgba(239, 68, 68, 0.1)',
                      padding: '8px',
                      borderRadius: '6px',
                      textDecoration: 'line-through'
                    }}>
                      {msg.original_bullet || 'Original text not available'}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', color: '#10b981', marginBottom: '4px', fontWeight: '600' }}>+ New:</div>
                    <div style={{
                      fontSize: '13px',
                      color: '#10b981',
                      background: 'rgba(16, 185, 129, 0.1)',
                      padding: '8px',
                      borderRadius: '6px',
                      fontWeight: '500'
                    }}>
                      {msg.edited_bullet}
                    </div>
                  </div>
                </div>

                {/* Accept/Reject Buttons */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button
                    onClick={() => {
                      if (onBulletUpdate) {
                        onBulletUpdate(msg.selected_bullet_info, msg.edited_bullet);
                      }
                      // Mark as accepted in message
                      setMessages(prev => prev.map((m, i) =>
                        i === index ? { ...m, edit_accepted: true } : m
                      ));
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    ✓ Accept Change
                  </button>
                  <button
                    onClick={() => {
                      // Just remove the edit suggestion
                      setMessages(prev => prev.map((m, i) =>
                        i === index ? { ...m, edited_bullet: null } : m
                      ));
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '8px',
                      color: '#ef4444',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(239, 68, 68, 0.25)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(239, 68, 68, 0.15)';
                    }}
                  >
                    ✕ Reject
                  </button>
                </div>

                {msg.edit_accepted && (
                  <div style={{
                    padding: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '6px',
                    color: '#10b981',
                    fontSize: '12px',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    ✓ Change Applied
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start'
          }}>
            <div style={{
              padding: '12px 16px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-light)',
              color: 'var(--text-tertiary)',
              fontSize: '14px',
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--indigo-500)',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}></div>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--indigo-500)',
                animation: 'pulse 1.5s ease-in-out 0.2s infinite'
              }}></div>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--indigo-500)',
                animation: 'pulse 1.5s ease-in-out 0.4s infinite'
              }}></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '20px',
        borderTop: '1px solid var(--border-light)',
        background: 'var(--bg-surface)'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-light)',
              borderRadius: '10px',
              color: 'var(--text-primary)',
              fontSize: '14px',
              resize: 'none',
              minHeight: '60px',
              maxHeight: '120px',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--indigo-500)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            style={{
              padding: '12px 24px',
              background: input.trim() && !isLoading
                ? 'var(--gradient-primary)'
                : 'rgba(255, 255, 255, 0.05)',
              color: input.trim() && !isLoading ? '#fff' : '#666',
              border: 'none',
              borderRadius: '10px',
              cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed',
              fontWeight: '600',
              fontSize: '14px',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              boxShadow: input.trim() && !isLoading ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'
            }}
          >
            Send
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default ChatBot;
